import React, { useCallback, useState } from 'react';

import { ClickableComponent } from '@schoolinks/ui-components';
import cn from 'classnames';
import { bool, func, number, shape, oneOf, string, node } from 'prop-types';

import { faArrowsUpDown } from '@fortawesome/pro-regular-svg-icons/faArrowsUpDown';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

import MuiDrawer from '@mui/material/Drawer';

import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';

import Text from 'components/Common/Text/deprecated/TextDeprecated';

import useMediaInfo from 'hooks/useMediaInfo';

import s from './Drawer.css';

const VERTICAL = 'vertical';
const HORIZONTAL = 'horizontal';
const VERTICAL_LEFT = 'vertical_left';

const Drawer = React.forwardRef(
  (
    {
      open,
      height,
      onClose,
      width = 1000,
      classes,
      transitionDuration,
      children,
      isResizable,
      variant,
      minResizableHeight,
      maxResizableHeight,
      minResizableWidth,
      maxResizableWidth,
      hideBackdrop,
      hideCloseButton,
      drawerVariant,
      contentClass,
      anchor,
      sx,
    },
    ref,
  ) => {
    const { isDesktop } = useMediaInfo();
    const isVertical = variant === VERTICAL || variant === VERTICAL_LEFT;
    const isVerticalLeft = variant === VERTICAL_LEFT;

    const [drawerHeight, setDrawerHeight] = useState(
      height || isVertical ? '100vh' : 'auto',
    );
    const [drawerWidth, setDrawerWidth] = useState(
      isVertical ? width : '100vw',
    );

    const handleMouseMove = useCallback(e => {
      if (!isVertical) {
        const newHeight = document.documentElement.clientHeight - e.clientY;
        if (newHeight > minResizableHeight && newHeight < maxResizableHeight) {
          setDrawerHeight(newHeight);
        }
        return;
      }

      const newWidth = document.body.offsetWidth - e.clientX;
      if (newWidth > minResizableWidth && newWidth < maxResizableWidth) {
        setDrawerWidth(newWidth);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleMouseUp = () => {
      document.removeEventListener('mouseup', handleMouseUp, true);
      document.removeEventListener('mousemove', handleMouseMove, true);
    };

    const handleMouseDown = () => {
      document.addEventListener('mouseup', handleMouseUp, true);
      document.addEventListener('mousemove', handleMouseMove, true);
    };

    const handleTouch = useCallback(e => {
      if (!isVertical) {
        const newHeight =
          document.documentElement.clientHeight - e.targetTouches[0].pageY;
        if (newHeight > minResizableHeight && newHeight < maxResizableHeight) {
          setDrawerHeight(newHeight);
        }
        return;
      }

      const newWidth = document.body.offsetWidth - e.targetTouches[0].pageX;
      if (newWidth > minResizableWidth && newWidth < maxResizableWidth) {
        setDrawerWidth(newWidth);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getCloseButtonPosition = () => {
      if (isDesktop) {
        const drawerWidthCalc =
          String(drawerWidth).includes('%') || String(drawerWidth).includes('v')
            ? drawerWidth
            : `${drawerWidth}px`;
        return isVerticalLeft
          ? { left: `calc(${drawerWidthCalc} + 8px)` }
          : { right: `calc(${drawerWidthCalc} + 8px)` };
      }

      return isVerticalLeft ? { left: 0 } : { right: 0 };
    };

    const renderCloseButton = () =>
      isVertical ? (
        <ClickableComponent
          className={s.closeButton}
          onClick={onClose}
          style={getCloseButtonPosition()}
        >
          <FontAwesomeIcon icon={faXmark} />
          {isDesktop && (
            <Text variant="caption" className={s.closeText}>
              ESC
            </Text>
          )}
        </ClickableComponent>
      ) : (
        <ClickableComponent
          className={s.closeButtonForHorizontal}
          onClick={onClose}
        >
          <FontAwesomeIcon size="2x" icon={faXmark} />
        </ClickableComponent>
      );

    const getAnchor = () => {
      if (anchor) return anchor;
      return isVertical ? 'right' : 'bottom';
    };

    return (
      <MuiDrawer
        anchor={getAnchor()}
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            width: drawerWidth,
            height: drawerHeight,
            left: isVerticalLeft ? 0 : null,
            overflow: 'unset',
          },
        }}
        classes={{
          paper: isVertical ? s.paperVertical : s.paperHorizontal,
          ...classes,
        }}
        ModalProps={{
          BackdropProps: {
            classes: { root: s.backdrop },
          },
        }}
        transitionDuration={transitionDuration}
        hideBackdrop={hideBackdrop}
        variant={drawerVariant}
        ref={ref}
        sx={sx}
      >
        {open ? (
          <>
            {hideCloseButton ? null : renderCloseButton()}
            <div
              className={cn(s.content, contentClass, {
                [s.contentVertical]: isVertical,
                [s.contentHorizontal]: !isVertical,
              })}
            >
              {children({ onClose, open })}
            </div>
            {isResizable && (
              // eslint-disable-next-line jsx-a11y/interactive-supports-focus
              <div
                onMouseDown={e => handleMouseDown(e)}
                onTouchMove={e => handleTouch(e)}
                className={cn(s.dragger, {
                  [s.draggerVertical]: isVertical,
                  [s.draggerHorizontal]: !isVertical,
                })}
                role="button"
              >
                <FontAwesomeIcon icon={faArrowsUpDown} />
              </div>
            )}
          </>
        ) : null}
      </MuiDrawer>
    );
  },
);

Drawer.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  children: func.isRequired,
  width: number,
  classes: shape({}),
  transitionDuration: shape({}),
  /* To use resize functionality */
  isResizable: bool,
  /* Variants for rendering the drawer vertical / horizontal / vertical (left side). Changes the anchor. */
  variant: oneOf([VERTICAL, HORIZONTAL, VERTICAL_LEFT]),
  /* Height props are used in the calculation for vertical resize (variant = VERTICAL / isResizable = true) */
  /* Minimum height that can drawer shrink */
  minResizableHeight: number,
  /* Maximum height that can drawer reach, we use document client height minus 10 to keep drag handle on screen */
  maxResizableHeight: number,
  /* Width props are used in the calculation for horizontal resize (variant = HORIZONTAL / isResizable = true) */
  /* Minimum width that can drawer shrink, */
  minResizableWidth: number,
  /* Maximum width that can drawer reach, we use document client width minus 64 to show sidebar. */
  maxResizableWidth: number,
  /* To hide half transparent black backdrop */
  hideBackdrop: bool,
  /* Hides the close button */
  hideCloseButton: bool,
  /* The variant for MUI drawer 8 */
  drawerVariant: string,
  /* Additional class used to overwrite s.content */
  contentClass: node,
  /* Side from which the drawer will appear. */
  anchor: string,
  /* Additional inline styles */
  sx: shape({}),
  height: number,
};

Drawer.defaultProps = {
  width: 1000,
  height: undefined,
  classes: {},
  transitionDuration: { enter: 300, exit: 300 },
  isResizable: false,
  variant: VERTICAL,
  minResizableHeight: 50,
  maxResizableHeight: document.documentElement.clientHeight - 10,
  minResizableWidth: 400,
  maxResizableWidth: document.body.clientWidth - 64,
  hideBackdrop: false,
  hideCloseButton: false,
  drawerVariant: 'temporary',
  contentClass: null,
  anchor: null,
  sx: undefined,
};

export default Drawer;
